import { useCallback, useRef, useState } from "react";

import { API_BASE } from "../api";
import { invalidateLivePassport } from "../passport-refresh";
import type { LoanProduct, Occupancy } from "../agent-types";
import type {
  ReadinessActiveResponse,
  ReadinessTargetKind,
  ReadinessTarget,
  ReadinessTimeline,
} from "../readiness-types";
import {
  parseTargetUnresolvableReason,
  TARGET_UNRESOLVABLE_CODE,
  TargetUnresolvableError,
} from "../target-unresolvable";

export interface TargetEditPatch {
  kind?: ReadinessTargetKind;
  price?: number;
  price_intent?: "buyer_selected" | "range_midpoint";
  shopping_range_revision?: number;
  location_code?: string;
  property_type?: string;
  occupancy?: Occupancy;
  units?: number;
  timeline?: ReadinessTimeline;
  estimated_taxes?: number;
  estimated_insurance?: number;
  hoa?: number;
  preferred_product?: LoanProduct;
  preferred_term_mo?: number;
  estimated_utilities?: number | null;
  estimated_maintenance?: number | null;
  applied_dpa_program_id?: string | null;
  seller_lender_credits_cents?: number;
  rate_override_bps?: number;
  discount_points?: number;
  label?: string;
  // Google place_id of the picked location suggestion (plan 454). Sent only
  // with a location edit; the server never returns it, so it cannot ride the
  // echoed draft and a price-only edit never carries a stale one.
  place_id?: string;
  property_id?: string | null;
  buyer_type?: string;
  savings_goal_pct?: number;
  savings_goal_amount?: number;
  down_payment_source?: string;
}

export interface UseTargetEditOptions {
  // Clerk JWT resolver. Same shape as useReadiness — pass the result of
  // `useAuth().getToken` or a wrapper that bumps a refresh nonce.
  getToken: () => Promise<string | null>;

  // Returns the current `target.id` from caller-owned state. Read each time
  // mutate() runs so a successful 409 rebase + caller state update can flow
  // back into the next attempt.
  getBaseTargetId: () => string | null | undefined;

  // Returns the current complete target request body in UI units (price in
  // dollars; Money-typed cost fields stay in cents). PUT /api/readiness/target
  // replaces the active target, so row editors merge their one-field patch
  // onto this draft before sending.
  //
  // May return undefined when the caller has no target to echo yet (its own
  // fetch hasn't resolved). Undefined spreads to `{}`, same as omitting the
  // option — deliberately NOT a partial draft, which would replace a real
  // deal with zero values.
  getTargetDraft?: () => TargetEditPatch | undefined;

  // Called after a 200 response. Caller updates its local snapshot/target
  // state and (typically) bumps a readiness-refresh nonce.
  onSuccess?: (data: ReadinessActiveResponse) => void;

  // Called after a 409 stale_target. Caller updates its local target state
  // with the rebased target. Does NOT auto-retry — the caller decides
  // whether to call mutate() again after updating state.
  onStaleRebase?: (latestTarget: ReadinessTarget) => void;
  // Refetch after a failure: the server may already have committed the write.
  onUncertain?: () => void;
}

export interface UseTargetEditResult {
  mutate: (patch: TargetEditPatch, options?: { baseTargetId: string }) => Promise<void>;
  isPending: boolean;
  error: Error | null;
}

export class StaleTargetError extends Error {
  readonly code = "stale_target" as const;
  constructor(public latestTarget: ReadinessTarget | null) {
    super("stale target");
    this.name = "StaleTargetError";
  }
}

export function useTargetEdit(opts: UseTargetEditOptions): UseTargetEditResult {
  const {
    getToken,
    getBaseTargetId,
    getTargetDraft,
    onSuccess,
    onStaleRebase,
    onUncertain,
  } = opts;
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Single-shot in-flight guard. A ref is used (not the `isPending` state) so
  // the guard reads the current value synchronously without churning
  // useCallback's identity on every toggle.
  const inFlightRef = useRef(false);

  const mutate = useCallback(
    async (patch: TargetEditPatch, options?: { baseTargetId: string }): Promise<void> => {
      // Drop new calls while a mutation is in flight. Phase 6c row components
      // fire mutate from onBlur, where rapid blurs are plausible. Callers that
      // need cancel-newest semantics should debounce in their own state.
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      setIsPending(true);
      setError(null);
      try {
        const token = await getToken();
        const baseTargetID = options?.baseTargetId ?? getBaseTargetId() ?? null;

        // Build body: include base_target_id only when non-null so that the
        // backend's `BaseTargetID *uuid.UUID` omitempty honesty contract holds.
        const body: Record<string, unknown> = {
          ...(getTargetDraft ? getTargetDraft() : {}),
          ...patch,
        };
        if (baseTargetID !== null) {
          body.base_target_id = baseTargetID;
        }

        const res = await fetch(`${API_BASE}/api/readiness/target`, {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const errBody = (await res.json().catch(() => ({}))) as Record<
            string,
            unknown
          >;
          if (res.status === 409 && errBody.code === "stale_target") {
            const latestTarget = (errBody.target as ReadinessTarget) ?? null;
            if (latestTarget && onStaleRebase) {
              try {
                onStaleRebase(latestTarget);
              } catch (cbErr) {
                // Caller bug — surface to console but still throw the typed
                // stale error so the mutation outcome is unambiguous.
                console.error(
                  "useTargetEdit onStaleRebase callback threw:",
                  cbErr,
                );
              }
            }
            throw new StaleTargetError(latestTarget);
          }
          // Plan 454: the location could not be placed. A typed error so the
          // row can render the reason copy instead of "try again" - retrying
          // the same text cannot succeed.
          if (res.status === 422 && errBody.code === TARGET_UNRESOLVABLE_CODE) {
            throw new TargetUnresolvableError(
              parseTargetUnresolvableReason(errBody.reason),
            );
          }
          throw new Error((errBody?.message as string) ?? `HTTP ${res.status}`);
        }

        const data = (await res.json()) as ReadinessActiveResponse;
        invalidateLivePassport();
        if (onSuccess) {
          try {
            onSuccess(data);
          } catch (cbErr) {
            // Network 200 succeeded; a callback throw is a caller bug, not a
            // mutation failure. Do not surface to error state.
            console.error("useTargetEdit onSuccess callback threw:", cbErr);
          }
        }
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        onUncertain?.();
        invalidateLivePassport();
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
        inFlightRef.current = false;
      }
    },
    [
      getToken,
      getBaseTargetId,
      getTargetDraft,
      onSuccess,
      onStaleRebase,
      onUncertain,
    ],
  );

  return { mutate, isPending, error };
}
